import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { TextField, Spinner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL } from "constants.js";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import { CustomField } from "types/CustomField";
import { Product } from "types/Product";
import DatePickerx from "features/datepicker/date-picker";
import { useAppDispatch } from "hooks";
import { fetchFileTemplateById } from "containers/files-template-form/files-template-form.service";
import { fetchFileDetails } from "containers/file/file.Slice";
import { fetchAuthorizedFileDetails } from "containers/file-authorized/authorized-file.Slice";
import { fetchById } from "services/upload-file.service";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { fetchFinanceDetailRefreshAsync } from "containers/finance-detail/finance-detail-container.slice";
import { fetchCategoryDetails } from "containers/document/category-tree/category-tree.Slice";
import { fetchCustomerOrdersList } from "features/customers-orders/customer-orders.slice";
import { fetchOrders } from "features/orders/orders.slice";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import FileTemplateSelect from "components/file-template-select/file-template-select";
import { useSignerSelection } from "../hooks/useSignerSelection";
import { useCustomFields } from "../hooks/useCustomFields";
import CustomFieldsRenderer from "../components/CustomFieldsRenderer";
import SignerPanel from "../components/SignerPanel";
import DocumentFormShell from "../components/DocumentFormShell";
import {
  transformOrderItem,
  transformProductGroup,
  validateForm,
  getParentTemplateMismatchErrorKey,
  buildPayload,
  BuildPdfFormProps,
  resolveEditBranch,
  normalizeBuildPdfTemplateId,
} from "../build-pdf-utils";
import { submitDocument, updateDocument } from "../build-pdf-api";
import { externalContractErrorMessage } from "utils/external-contract-errors";

export default function OrderDocumentForm({
  customerId,
  isCustomerApproved = false,
  template,
  fileId,
  assignedUserId,
  id,
  subject,
  user,
  signType: signTypeProps,
  managerSigner,
  orderEntity,
  year,
  financeId,
  lockedTemplateId,
  isOrderAmendment,
  orderId: orderIdProp,
  orderAmendmentFileType,
  onSuccess,
}: BuildPdfFormProps) {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const dispatch = useAppDispatch();

  const [templateId, setTemplateId] = useState(() => {
    const normalizedLockedTemplateId =
      normalizeBuildPdfTemplateId(lockedTemplateId);
    if (id && orderAmendmentFileType) return "";
    if (id) return normalizedLockedTemplateId || "";
    // Create: only lock pre-fills. `template` is often the finance *parent* for child-only dropdown — never use as selected id.
    if (normalizedLockedTemplateId) return normalizedLockedTemplateId;
    return "";
  });
  const [date, setDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [productGroups, setProductGroups] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  // Display-ready text (already translated) for the last failed submit — most
  // relevantly the amendment-slot refusal (files/build-pdf), which used to be
  // swallowed behind a generic "error" banner.
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [fileTypeContext, setFileTypeContext] = useState("");
  const [templateIdByFetch, setTemplateIdByFetch] = useState("");
  const [orderId, setOrderId] = useState<any>(orderIdProp ?? null);

  const [systemTemplateFields, setSystemTemplateFields] = useState<
    CustomField[] | null
  >(null);
  const [systemTemplateSource, setSystemTemplateSource] = useState<
    string | null
  >(null);
  const [loadingSystemFields, setLoadingSystemFields] = useState(false);
  const [isEditingSystemTemplate, setIsEditingSystemTemplate] = useState(false);

  const isOrderAmendmentSystemCreate =
    !id &&
    !!isOrderAmendment &&
    orderAmendmentFileType === "order_amendment";
  const isSystemSource =
    isOrderAmendmentSystemCreate && systemTemplateSource === "system";
  const hasSystemFields =
    isSystemSource && (systemTemplateFields?.length ?? 0) > 0;

  const {
    selectedUser, setSelectedUser,
    selectedContactIds, setSelectedContactIds,
    signType, setSignType,
    selectedNonTechUser, setSelectedNonTechUser,
    selectedTabUser, setSelectedTabUser,
    signCollection, handleSignType, hydrateSigners,
  } = useSignerSelection({
    signTypeProps,
    managerSigner,
    assignedUserId,
    isEditMode: !!id,
  });

  const {
    customFields, setCustomFields,
    templateEntity, setTemplateEntity,
    fileEntitiesMap,
    handleCustomFieldChange,
    transformCustomFields,
    setInitialCustomFieldsWhenEditLoaded,
    setInitialTemplateEntityWhenEditLoaded,
  } = useCustomFields({
    companyId,
    templateId,
    templateIdByFetch,
    isEditMode: !!id,
    onSignTypeDetected: setSignType,
  });

  useEffect(() => {
    const lock = normalizeBuildPdfTemplateId(lockedTemplateId);
    if (lock && !id) setTemplateId(lock);
  }, [lockedTemplateId, id]);

  useEffect(() => {
    if (orderEntity?.items?.length) {
      setProducts(orderEntity.items.map(transformOrderItem));
    }
    if (orderEntity?.productGroups?.length) {
      setProductGroups(orderEntity.productGroups.map(transformProductGroup));
    }
    if (orderEntity?.id) setOrderId(orderEntity.id);
  }, [JSON.stringify(orderEntity)]);

  useEffect(() => {
    if (!isOrderAmendmentSystemCreate || !companyId || !orderAmendmentFileType)
      return;
    let cancelled = false;
    setLoadingSystemFields(true);
    axios
      .get(
        `${API_URL}/${companyId}/system-template-fields/${orderAmendmentFileType}`
      )
      .then((res) => {
        if (cancelled) return;
        const { fields = [], source } = res.data;
        if (source === "custom") {
          setSystemTemplateSource("custom");
          setSystemTemplateFields([]);
        } else {
          setSystemTemplateSource("system");
          setSystemTemplateFields(fields);
          if (fields.length > 0) {
            setCustomFields(
              fields.map((f: CustomField) => ({
                ...f,
                value: (f as { isMulti?: boolean }).isMulti ? [] : "",
              }))
            );
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSystemTemplateSource("system");
          setSystemTemplateFields([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSystemFields(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOrderAmendmentSystemCreate, companyId, orderAmendmentFileType]);

  useEffect(() => {
    customFields.map((field, index) => {
      if (field.type === "products" && templateId && orderId && products.length > 0) {
        handleCustomFieldChange(index, JSON.stringify(products));
      }
      if (field.type === "product_group" && templateId && orderId && productGroups.length > 0) {
        handleCustomFieldChange(index, JSON.stringify(productGroups) as any);
      }
      if (
        field.type === "products_or_groups" &&
        templateId &&
        orderId &&
        (products.length > 0 || productGroups.length > 0)
      ) {
        handleCustomFieldChange(
          index,
          JSON.stringify({ products, productGroups }) as any
        );
      }
      return;
    });
  }, [templateId, orderId, products, productGroups, customFields.length]);

  useEffect(() => {
    if (!id) return;
    async function fetchData() {
      try {
        const {
          date,
          templateId: templateId_,
          signType,
          filesTemplateCustomField,
          fileTypeContext,
          signersList,
          order,
        } = await fetchById(id, companyId);
        const editBranch = resolveEditBranch(
          templateId_?.id,
          undefined,
          companyId,
          orderAmendmentFileType
        );
        if (editBranch === "template" && templateId_?.id) {
          const fetchedTemplateId = templateId_?.id;
          setTemplateId(fetchedTemplateId);
          setTemplateIdByFetch(fetchedTemplateId);

          const fileTemplateEntity = await fetchFileTemplateById(
            companyId,
            fetchedTemplateId
          );
          setTemplateEntity(fileTemplateEntity);
          const transformedFields = await transformCustomFields(
            filesTemplateCustomField?.customFields,
            fileTemplateEntity.customFields
          );
          const loadedCustomFields = fileTemplateEntity.customFields.map(
            (field: CustomField) => ({
              ...field,
              value:
                field.type === "file"
                  ? transformedFields[field.label] || []
                  : transformedFields[field.label] || "",
            })
          );
          setCustomFields(loadedCustomFields);
          setInitialCustomFieldsWhenEditLoaded(loadedCustomFields);
          setInitialTemplateEntityWhenEditLoaded(fileTemplateEntity);

          if (order?.id) {
            setProducts(order?.orderItems?.map(transformOrderItem) || []);
            setProductGroups(
              (order as any)?.productGroups?.map(transformProductGroup) || []
            );
          } else {
            const productsOrGroupsField = fileTemplateEntity.customFields.find(
              (f: CustomField) => f.type === "products_or_groups"
            );
            if (productsOrGroupsField) {
              const combinedData =
                transformedFields[productsOrGroupsField.label];
              if (combinedData) {
                try {
                  const parsed =
                    typeof combinedData === "string"
                      ? JSON.parse(combinedData)
                      : combinedData;
                  if (parsed.products) setProducts(parsed.products);
                  if (parsed.productGroups)
                    setProductGroups(parsed.productGroups);
                } catch (_e) {
                  setProducts(filesTemplateCustomField?.products || []);
                  setProductGroups(
                    (filesTemplateCustomField as any)?.productGroups || []
                  );
                }
              } else {
                setProducts(filesTemplateCustomField?.products || []);
                setProductGroups(
                  (filesTemplateCustomField as any)?.productGroups || []
                );
              }
            } else {
              setProducts(filesTemplateCustomField?.products || []);
              setProductGroups(
                (filesTemplateCustomField as any)?.productGroups || []
              );
            }
          }
          setOrderId(order?.id || null);
        } else if (editBranch === "system-template" && orderAmendmentFileType) {
          setIsEditingSystemTemplate(true);
          setTemplateId("");
          setTemplateIdByFetch("");
          try {
            const sysRes = await axios.get(
              `${API_URL}/${companyId}/system-template-fields/${orderAmendmentFileType}`
            );
            const sysFields: CustomField[] = sysRes.data.fields || [];
            if (sysFields.length > 0) {
              const savedValues =
                filesTemplateCustomField?.customFields || {};
              setCustomFields(
                sysFields.map((f: CustomField) => ({
                  ...f,
                  value:
                    savedValues[f.label] ??
                    ((f as { isMulti?: boolean }).isMulti ? [] : ""),
                }))
              );
            }
          } catch (_e) {
            // Registry unavailable
          }
          setOrderId(order?.id || null);
        }
        setSignType(signType);
        setDate(date);
        setFileTypeContext(fileTypeContext);
        hydrateSigners(signersList);
      } catch (_e) {
        // silently ignore
      }
    }
    fetchData();
  }, [id, companyId, orderAmendmentFileType]);

  // Same idea as UserDocumentForm / BusinessTripDocumentForm: parent mismatch only when
  // `template` is the finance root for child-only dropdown — not when lockedTemplateId pins the real template.
  const normalizedLock = normalizeBuildPdfTemplateId(lockedTemplateId);
  const parentTemplateId =
    !id &&
    !normalizedLock &&
    !isSystemSource &&
    !isEditingSystemTemplate
      ? normalizeBuildPdfTemplateId(template)
      : "";

  const isFormValid = () => {
    let validationErrors = validateForm({
      templateId,
      date,
      subject,
      selectedContactIds,
      signType,
      selectedUser,
      selectedNonTechUser,
      isSystemSource,
      isEditingSystemTemplate,
      isAutoBusinessTripDocument: false,
      customFields,
      products,
      productGroups,
    });
    const parentMismatch = getParentTemplateMismatchErrorKey({
      templateId,
      parentTemplateId,
      isSystemSource,
      isEditingSystemTemplate,
    });
    if (parentMismatch) {
      validationErrors = { ...validationErrors, templateId: parentMismatch };
    }
    const translatedErrors: { [key: string]: string } = {};
    for (const [key, val] of Object.entries(validationErrors)) {
      translatedErrors[key] =
        typeof val === "string" ? t(val) : t(val.key, val.params);
    }
    setErrors(translatedErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!isFormValid()) return;
    const formData = buildPayload({
      date,
      templateId,
      customerId,
      customFields,
      products,
      productGroups,
      fileId,
      selectedContactIds,
      selectedUser,
      selectedNonTechUser,
      signType,
      selectedTabUser,
      fileTypeContext,
      user,
      orderId,
      fuelReportId: null,
      businessTripId: null,
      financeId,
      isOrderAmendment,
      ...(orderAmendmentFileType
        ? { orderAmendmentFileType }
        : {}),
    });

    if (!companyId) return;
    try {
      setIsFailed(false);
      setFailureMessage(null);
      setIsLoading(true);
      const action = id ? updateDocument : submitDocument;
      const fileEntity = await action(companyId, formData, id);
      const savedFileId = fileEntity?.id ?? fileEntity?.data?.id;
      if (fileId) dispatch(fetchFileDetails({ fileId, companyId }));
      if (savedFileId) {
        if (customerId)
          dispatch(fetchCustomer({ companyId, customerId }));
        if (customerId && year) {
          dispatch(
            fetchFinanceDetailRefreshAsync({
              companyId,
              customerId,
              year,
              financeId,
            })
          );
        }
        dispatch(fetchCategoryDetails({ companyId, source: "profile" }));
        dispatch(fetchFileDetails({ fileId: savedFileId, companyId }));
        dispatch(
          fetchAuthorizedFileDetails({ fileId: savedFileId, companyId })
        );
        const effectiveOrderId = orderId ?? orderIdProp;
        if (effectiveOrderId && companyId) {
          dispatch(fetchCustomerOrdersList(companyId, customerId));
          dispatch(fetchOrders({ filterValue: {}, companyId }));
          dispatch(fetchOrderDetails({ companyId, orderId: effectiveOrderId }));
        }
        onSuccess?.();
        setIsLoading(false);
        setIsFailed(false);
        setIsSuccessful(true);
        setTimeout(() => {
          dispatch(setIsModalxOpen(false));
        }, 1500);
      } else {
        setIsFailed(true);
        setIsLoading(false);
      }
    } catch (e) {
      setIsFailed(true);
      setFailureMessage(externalContractErrorMessage(e, t));
      setIsLoading(false);
    }
  };

  const showTemplateChrome =
    templateId ||
    isSystemSource ||
    isEditingSystemTemplate ||
    hasSystemFields;

  return (
    <DocumentFormShell
      onSubmit={handleFormSubmit}
      isLoading={isLoading}
      isFailed={isFailed}
      failureMessage={failureMessage}
      isSuccessful={isSuccessful}
      submitDisabled={loadingSystemFields}
    >
      {loadingSystemFields && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "12px 0",
          }}
        >
          <Spinner size="small" />
        </div>
      )}
      {!normalizeBuildPdfTemplateId(lockedTemplateId) &&
        !isSystemSource &&
        !isEditingSystemTemplate &&
        !loadingSystemFields &&
        !(id && !templateId) && (
        <>
          <FileTemplateSelect
            onChange={setTemplateId}
            value={templateId}
            isRequired={true}
            isCustomerApproved={isCustomerApproved}
            templateId={
              id ? "" : normalizeBuildPdfTemplateId(template) || templateIdByFetch
            }
            error={errors["templateId"]}
            subject={subject}
          />
          {templateId && (
            <TextField
              label={t("file_context")}
              type="text"
              autoComplete="off"
              value={fileTypeContext}
              onChange={setFileTypeContext}
              labelHidden
              placeholder={t("enter_file_context")}
            />
          )}
        </>
      )}
      {showTemplateChrome && (
        <DatePickerx
          disableDatesAfter={null}
          title={t("document_date")}
          isRequired={true}
          onChange={(value: any) => setDate(value.toISOString().split("T")[0])}
          selected={date}
          readOnly={false}
        />
      )}
      <CustomFieldsRenderer
        customFields={customFields}
        errors={errors}
        onFieldChange={handleCustomFieldChange}
        products={products}
        productGroups={productGroups}
        setProducts={setProducts}
        setProductGroups={setProductGroups}
        orderId={orderId}
        templateId={templateId || (isSystemSource || isEditingSystemTemplate ? "system" : "")}
        customerId={customerId}
        fileEntitiesMap={fileEntitiesMap}
      />
      {showTemplateChrome && (
        <SignerPanel
          customerId={customerId}
          selectedUser={selectedUser}
          onUserChange={setSelectedUser}
          selectedContactIds={selectedContactIds}
          onContactChange={setSelectedContactIds}
          signType={signType}
          onSignTypeChange={handleSignType}
          selectedNonTechUser={selectedNonTechUser}
          onNonTechUserChange={setSelectedNonTechUser}
          selectedTabUser={selectedTabUser}
          onTabUserChange={setSelectedTabUser}
          signCollection={signCollection}
          errors={errors}
        />
      )}
    </DocumentFormShell>
  );
}
